import { Location } from 'history'
import { parse, stringify } from 'query-string'
import { useContext, useMemo, useCallback } from 'react'
import { __RouterContext as RouterContext, RouteComponentProps } from 'react-router'
import uriTemplate, { URITemplate } from 'uri-templates'

// refactor on react-router v6
export const useRouter = <T extends { [K in keyof T]?: string | undefined }>(): RouteComponentProps<T> =>
  useContext(RouterContext) as RouteComponentProps<T>

export const useLocation = (): Location => {
  const { location } = useRouter()
  return location
}

export const useParams = <T extends { [K in keyof T]?: string | undefined }>(): T => {
  const { match } = useRouter<T>()
  return match.params
}

interface ParsedQuery<T = string> {
  [key: string]: T | Array<T> | null | undefined
}

export const useQuery = <T extends ParsedQuery>(): T => {
  const { search } = useLocation()
  const query = useMemo(() => parse(search), [search])
  return query as T
}

interface UpdateQueryOptions {
  replace: boolean
}

type UpdateQuery<T> = (patch: Partial<T>) => void

type Visit<T> = (params: T) => void

const USE_PUSH = { replace: false }

export const useUpdateQuery = <T extends ParsedQuery>(options: UpdateQueryOptions = USE_PUSH): UpdateQuery<T> => {
  const { history } = useRouter()
  const query = useQuery<T>()
  const { replace } = options
  const updateQuery = useCallback(
    (patch: Partial<T>): void => {
      const newQuery = { ...query, ...patch }
      const newSearch = stringify(newQuery)
      if (replace) {
        history.replace({ search: newSearch })
      } else {
        history.push({ search: newSearch })
      }
    },
    [history, query, replace]
  )
  return updateQuery
}

export const useNavigate = <T>(to: string | URITemplate, options: UpdateQueryOptions = USE_PUSH): Visit<T> => {
  const { history } = useRouter()
  const { replace } = options
  const template = useMemo(() => {
    if (typeof to === 'string') {
      return uriTemplate(to)
    }
    return to
  }, [to])
  const visit = useCallback(
    (params: T): void => {
      const newLocation = template.fill(params as any)
      if (replace) {
        history.replace(newLocation)
      } else {
        history.push(newLocation)
      }
    },
    [template, history, replace]
  )
  return visit
}
